const SECOND = 1000;
const MINUTE = SECOND * 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;
const TICK = 10000;

/**
 * @see https://docs.microsoft.com/en-us/dotnet/api/system.timespan?view=net-5.0
 */
export class TimeSpan {

    static TICK = TICK;
    static TicksPerSecond = TICK * 1000;

    _data = [0, 0, 0, 0, 0, 0];
    _ticks = 0;

    constructor(...args) {
        this._parseArgs(args);
    }

    // Gets the days component of the time interval represented by the current TimeSpan structure.
    get Days() {
        return this._data[0];
    }

    // Gets the hours component of the time interval represented by the current TimeSpan structure.
    get Hours() {
        return this._data[1];
    }

    // Gets the milliseconds component of the time interval represented by the current TimeSpan structure.
    get Milliseconds() {
        return this._data[4];
    }

    // Gets the minutes component of the time interval represented by the current TimeSpan structure.
    get Minutes() {
        return this._data[2];
    }

    // Gets the seconds component of the time interval represented by the current TimeSpan structure.
    get Seconds() {
        return this._data[3];
    }

    // Gets the number of ticks that represent the value of the current TimeSpan structure.
    get Ticks() {
        return this._ticks;
    }

    // Gets the value of the current TimeSpan structure expressed in whole and fractional days.
    get TotalDays() {
        return (this._ticks / TimeSpan.TICK) / DAY;
    }

    // Gets the value of the current TimeSpan structure expressed in whole and fractional hours.
    get TotalHours() {
        return (this._ticks / TimeSpan.TICK) / HOUR;
    }

    // Gets the value of the current TimeSpan structure expressed in whole and fractional milliseconds.
    get TotalMilliseconds() {
        return (this._ticks / TimeSpan.TICK);
    }

    // Gets the value of the current TimeSpan structure expressed in whole and fractional minutes.
    get TotalMinutes() {
        return (this._ticks / TimeSpan.TICK) / MINUTE;
    }

    // Gets the value of the current TimeSpan structure expressed in whole and fractional seconds.
    get TotalSeconds() {
        return (this._ticks / TimeSpan.TICK) / SECOND;
    }

    // Returns a TimeSpan that represents a specified number of days, where the specification is accurate to the nearest millisecond.
    static FromDays(val) {
        return new TimeSpan(val, 0, 0, 0, 0);
    }

    // Returns a TimeSpan that represents a specified number of hours, where the specification is accurate to the nearest millisecond.
    static FromHours(val) {
        return new TimeSpan(0, val, 0, 0, 0);
    }

    valueOf() {
        return this._ticks;
    }

    toString() {
        let [d, h, m, s, ms, t] = this._data;
        return (d !== 0 ? d + "." : "") + [
            h.toString().padStart(2, "0"),
            m.toString().padStart(2, "0"),
            s.toString().padStart(2, "0"),
        ].join(":") + "." + (ms * TimeSpan.TICK + t);
    }

    _parseArgs(args) {
        if (args.length > 0) {
            if (![1, 3, 4, 5].includes(args.length)) {
                throw new Error("Invalid TimeSpan constructor arguments count: " + args.join(", "));
            }
            let data = args.length === 1
                ? [0, 0, 0, 0, 0, ~~args[0]]
                : [0, 0, 0, 0, 0, ...args, 0].slice(args.length, args.length + 6)
            ;
            this._updateData(data);
        }
    }

    _updateData(data = this._data) {
        let [d, h, m, s, ms, t] = data, delta;

        delta = ~~(t / TimeSpan.TICK);
        ms += delta * TimeSpan.TICK;
        t -= delta * TimeSpan.TICK;

        delta = ~~(ms / 1000);
        s += delta * 1000;
        ms -= delta * 1000;

        delta = ~~(s / 60);
        m += delta * 60;
        s -= delta * 60;

        delta = ~~(m / 60);
        h += delta * 60;
        m -= delta * 60;

        delta = ~~(h / 24);
        d += delta * 24;
        h -= delta * 24;

        this._data = [d, h, m, s, ms, t];
        this._ticks = (d * DAY + h * HOUR + m * MINUTE + s * SECOND + ms) * TimeSpan.TICK + t;
    }

    // Returns a new TimeSpan object whose value is the sum of the specified TimeSpan object and this instance.
    Add(t) {
        return new TimeSpan((this._ticks + t) || 0);
    }

    // Compares two TimeSpan values and returns an integer that indicates whether the first value is shorter than, equal to, or longer than the second value.
    Compare(other) {
        return Math.sign(this - other) || 0;
    }

    // Returns a new TimeSpan object which value is the result of division of this instance and the specified divisor.
    Divide(t) {
        return new TimeSpan(~~(this._ticks / t));
    }

    // Returns a new TimeSpan object whose value is the absolute value of the current TimeSpan object.
    Duration() {
        return new TimeSpan(this._ticks);
    }

    // Returns a value indicating whether this instance is equal to a specified object.
    Equals(t) {
        return (this * 1) === (t * 1);
    }

    // Returns a TimeSpan that represents a specified number of milliseconds.
    FromMilliseconds(val) {
        return new TimeSpan(0, 0, 0, 0, val);
    }

    // Returns a TimeSpan that represents a specified number of minutes, where the specification is accurate to the nearest millisecond.
    FromMinutes(val) {
        return new TimeSpan(0, 0, val, 0, 0);
    }

    // Returns a TimeSpan that represents a specified number of seconds, where the specification is accurate to the nearest millisecond.
    FromSeconds(val) {
        return new TimeSpan(0, 0, 0, val, 0);
    }

    // Returns a TimeSpan that represents a specified time, where the specification is in units of ticks.
    FromTicks(val) {
        return new TimeSpan(val);
    }

    // Returns a new TimeSpan object which value is the result of multiplication of this instance and the specified factor.
    Multiply(t) {
        return new TimeSpan(~~(this._ticks * t));
    }

    // Returns a new TimeSpan object whose value is the negated value of this instance.
    Negate() {
        return new TimeSpan(-this._ticks);
    }

    // Returns a new TimeSpan object whose value is the difference between the specified TimeSpan object and this instance.
    Subtract(t) {
        return new TimeSpan(~~(this._ticks - t));
    }

}
